import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useLang, useT } from "../lib/i18n";
import PWAInstallGuide from "./PWAInstallGuide";
import WhatsAppFloating from "./WhatsAppFloating";
import WhatsAppIcon from "./WhatsAppIcon";
import { HeaderLogo, FooterLogo } from "./BrandLogos";
import RoadRibbon from "./RoadRibbon";

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState({ name: "Shruti Tours & Travels", phone: "+91 9890201047", email: "shrutitandt@gmail.com", tagline: "Safe Travels, Happy Memories" });
  const { lang, setLang } = useLang();
  const t = useT();
  const loc = useLocation();

  useEffect(() => { api.get("/company").then(r => setCompany(r.data)).catch(()=>{}); }, []);

  const nav = [
    { to: "/", labelKey: "nav.home" },
    { to: "/routes", labelKey: "nav.daily_routes" },
    { to: "/tours", labelKey: "nav.upcoming_tours" },
    { to: "/vehicles", labelKey: "nav.fleet" },
    { to: "/book", labelKey: "nav.book_now" },
    { to: "/custom-booking", labelKey: "nav.custom_trip" },
    { to: "/lookup", labelKey: "nav.my_booking" },
    { to: "/about", labelKey: "nav.about" },
    { to: "/contact", labelKey: "nav.contact" },
  ];
  const waNumber = (company.phone || "+91 9890201047").replace(/\D/g,'');

  const LangToggle = ({ testid = "lang-toggle" }) => (
    <div className="flex items-center rounded-full bg-slate-100 border border-slate-200 p-0.5 text-[10px] font-bold" data-testid={testid}>
      <button onClick={() => setLang("en")} className={`px-2 py-0.5 rounded-full transition-colors ${lang === "en" ? "bg-[#1e1b4b] text-white" : "text-slate-600 hover:text-[#1e1b4b]"}`} data-testid="lang-en">EN</button>
      <button onClick={() => setLang("mr")} className={`px-2 py-0.5 rounded-full transition-colors ${lang === "mr" ? "bg-[#ea580c] text-white" : "text-slate-600 hover:text-[#ea580c]"}`} data-testid="lang-mr">MR</button>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top strip */}
      <div className="bg-[var(--brand-navy)] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <a href={`tel:${company.phone}`} className="flex items-center gap-1.5 hover:text-orange-300 whitespace-nowrap" data-testid="topbar-phone"><Phone className="w-3 h-3" /> {company.phone}</a>
            <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 hover:text-emerald-300 whitespace-nowrap" data-testid="topbar-whatsapp"><WhatsAppIcon className="w-3.5 h-3.5" /> {t("hero.whatsapp")}</a>
            <a href={`mailto:${company.email}`} className="hidden md:flex items-center gap-1.5 hover:text-orange-300 whitespace-nowrap" data-testid="topbar-email"><Mail className="w-3 h-3" /> {company.email}</a>
          </div>
          <div className="italic text-amber-300/90 truncate hidden md:block">✦ {t("topbar.tagline")} ✦</div>
        </div>
      </div>
      <div className="header-stripe" />

      {/* Header road ribbon */}
      <RoadRibbon reverse text={t("ribbon.header")} testid="header-road-ribbon" />

      {/* Sticky header — lang toggle lives here so it stays visible on scroll */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-14 lg:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <HeaderLogo size="sm" />
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5 flex-nowrap">
            {nav.map(n => (
              <Link key={n.to} to={n.to}
                data-testid={`nav-${n.labelKey.replace(/\./g,'-')}`}
                className={`px-2.5 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${loc.pathname===n.to ? "bg-[var(--brand-navy)] text-white" : "text-slate-700 hover:bg-slate-100"}`}>{t(n.labelKey)}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LangToggle />
            <Link to="/login" className="hidden lg:inline-block px-3 py-1.5 rounded-full text-[13px] font-semibold text-[var(--brand-navy)] hover:bg-slate-100 whitespace-nowrap" data-testid="login-link">{t("nav.login")}</Link>
            <Link to="/book" className="hidden lg:inline-block px-4 py-2 rounded-full text-[13px] font-bold bg-[var(--brand-orange)] text-white hover:bg-[var(--brand-orange-2)] transition-colors shadow-md shadow-orange-200 whitespace-nowrap" data-testid="book-now-cta">{t("nav.book_now")}</Link>
            <button className="lg:hidden p-2" onClick={()=>setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t bg-white">
            <div className="p-4 grid gap-1">
              {nav.map(n => <Link key={n.to} to={n.to} onClick={()=>setOpen(false)} className="px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-700 font-medium" data-testid={`mobile-nav-${n.labelKey.replace(/\./g,'-')}`}>{t(n.labelKey)}</Link>)}
              <Link to="/login" className="px-3 py-2 rounded-lg hover:bg-slate-100 font-medium" data-testid="mobile-login-link">{t("nav.login")}</Link>
              <Link to="/book" onClick={()=>setOpen(false)} className="mt-1 px-3 py-2 rounded-lg bg-[var(--brand-orange)] text-white text-center font-bold" data-testid="mobile-book-cta">{t("nav.book_now")}</Link>            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <div className="footer-band" />
      <RoadRibbon text={t("ribbon.footer")} />

      <footer className="bg-[var(--brand-navy)] text-slate-300">
        <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-4 gap-8">
          <div>
            <FooterLogo />
            <p className="text-sm mt-4 leading-relaxed">{t("footer.about_line")}</p>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-white/10 border border-white/10">{t("footer.badge_safe")}</span>
              <span className="px-2 py-1 rounded-full bg-white/10 border border-white/10">{t("footer.badge_drivers")}</span>
            </div>
          </div>
          <div>
            <div className="font-semibold text-white mb-3">{t("footer.explore")}</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/routes" className="hover:text-orange-300">{t("footer.link.routes")}</Link></li>
              <li><Link to="/tours" className="hover:text-orange-300">{t("footer.link.tours")}</Link></li>
              <li><Link to="/vehicles" className="hover:text-orange-300">{t("footer.link.fleet")}</Link></li>
              <li><Link to="/book" className="hover:text-orange-300">{t("footer.link.book")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-white mb-3">{t("footer.support")}</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/faq" className="hover:text-orange-300">{t("footer.link.faq")}</Link></li>
              <li><Link to="/lookup" className="hover:text-orange-300">{t("footer.link.track")}</Link></li>
              <li><Link to="/contact" className="hover:text-orange-300">{t("footer.link.contact")}</Link></li>
              <li><Link to="/admin/login" className="hover:text-orange-300">{t("footer.link.admin")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-white mb-3">{t("footer.contact_booking")}</div>
            <div className="text-sm space-y-2">
              <a href={`tel:${company.phone}`} className="flex items-center gap-2 hover:text-orange-300"><Phone className="w-4 h-4 text-orange-400" /> {company.phone}</a>
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-emerald-300"><WhatsAppIcon className="w-4 h-4 text-[#25D366]" /> {t("hero.whatsapp")}</a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-orange-300"><Mail className="w-4 h-4 text-orange-400" /> {company.email}</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-slate-400">© {new Date().getFullYear()} {company.name} · {t("topbar.tagline")}</div>
      </footer>
      <PWAInstallGuide />
      <WhatsAppFloating />
    </div>
  );
}
